import { Descendant, Node, Text } from "slate";

// DOMParser를 사용해 HTML 문자열 → Document
export function deserializeHTML(html: string): Descendant[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const body = doc.body;
  const nodes = Array.from(body.childNodes).map(deserializeElement).flat();

  return nodes;
}

function deserializeElement(domNode: globalThis.Node): Descendant[] {
  // 1) TEXT_NODE
  if (domNode.nodeType === globalThis.Node.TEXT_NODE) {
    const textContent = domNode.textContent ?? "";
    if (textContent.trim() === "") {
      return [];
    }
    return [{ text: textContent }];
  }

  // 2) ELEMENT_NODE
  if (domNode.nodeType === globalThis.Node.ELEMENT_NODE) {
    const element = domNode as HTMLElement;
    const tagName = element.tagName.toLowerCase();

    // 자식 역직렬화
    let children = Array.from(element.childNodes)
      .map(deserializeElement)
      .flat();
    if (children.length === 0) {
      children = [{ text: "" }];
    }

    // 인라인 style 파싱
    const styleMap = parseStyle(element.getAttribute("style") || "");

    // tagName별 처리
    switch (tagName) {
      case "p": {
        // text-align
        const textAlign = styleMap["text-align"];
        return [
          {
            type: "paragraph",
            textAlign,
            children,
          },
        ];
      }

      case "a": {
        // 링크
        const href = element.getAttribute("href") || "";
        return [
          {
            type: "link",
            url: href,
            children,
          },
        ];
      }

      case "span": {
        // span 안에 font-weight, text-decoration, color, font-size 등 있을 수 있음
        const { bold, underline, color, fontSize } = styleMapToMarks(styleMap);
        // span 내부의 모든 children에 mark를 적용
        children = applyMarks(children, { bold, underline, color, fontSize });
        return children;
      }

      case "strong": {
        // 굵게
        children = applyMarks(children, { bold: true });
        return children;
      }

      case "u": {
        // 밑줄
        children = applyMarks(children, { underline: true });
        return children;
      }

      case "img": {
        // 이미지
        const src = element.getAttribute("src") || "";
        // text-align 처리 가능 (부모 div나 style에서 직접 적용되는 경우도 있으나, 여기서는 img 태그의 inline style 사용)
        const textAlign = styleMap["text-align"];
        return [
          {
            type: "image",
            url: src,
            children: [{ text: "" }], // 이미지 노드는 보통 children이 빈 텍스트
          },
        ];
      }

      default:
        // 태그 매칭 안 되면 p로 처리
        return [
          {
            type: "paragraph",
            children,
          },
        ];
    }
  }

  return [];
}

/**
 * 인라인 style="font-weight: bold; color: red; ..." 파싱
 */
function parseStyle(styleAttr: string): Record<string, string> {
  const styleMap: Record<string, string> = {};
  styleAttr.split(";").forEach((item) => {
    const [key, val] = item.split(":").map((x) => x.trim());
    if (key && val) {
      styleMap[key] = val;
    }
  });
  return styleMap;
}

/**
 * styleMap → { bold: true, underline: true, color: "red", fontSize: "20px" } 추출
 */
function styleMapToMarks(
  styleMap: Record<string, string>
): { bold?: boolean; underline?: boolean; color?: string; fontSize?: string } {
  const result: any = {};

  // font-weight
  if (styleMap["font-weight"] && styleMap["font-weight"] !== "normal") {
    result.bold = true;
  }
  // text-decoration
  if (styleMap["text-decoration"] && styleMap["text-decoration"].includes("underline")) {
    result.underline = true;
  }
  // color
  if (styleMap["color"]) {
    result.color = styleMap["color"];
  }
  // font-size
  if (styleMap["font-size"]) {
    result.fontSize = styleMap["font-size"];
  }

  return result;
}

/**
 * children(Descendant[]) 모든 Text에 markProps를 병합
 */
function applyMarks(
  nodes: Descendant[],
  markProps: { bold?: boolean; underline?: boolean; color?: string; fontSize?: string }
): Descendant[] {
  return nodes.map((n) => {
    if (Text.isText(n)) {
      return { ...n, ...markProps };
    } else if ("children" in n) {
      // 재귀 적용
      const newChildren = applyMarks(n.children, markProps);
      return { ...n, children: newChildren };
    }
    return n;
  });
}
